import { useState, useEffect } from "react";
import ContactModal from "./Contact";

const CTA = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const styles = {
    section: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: isMobile ? '40px 20px' : '60px 40px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 1,
    },
    title: {
      fontSize: isMobile ? 'clamp(22px, 6vw, 28px)' : 'clamp(32px, 4vw, 42px)',
      fontWeight: '800',
      color: '#ffffff',
      marginBottom: isMobile ? '12px' : '16px',
      lineHeight: '1.2',
      textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
      padding: '0 10px',
    },
    description: {
      fontSize: isMobile ? '13px' : 'clamp(15px, 1.8vw, 17px)',
      color: '#ffffff',
      lineHeight: '1.6',
      opacity: '0.95',
      maxWidth: isMobile ? '100%' : '650px',
      margin: isMobile ? '0 auto 24px' : '0 auto 28px',
      padding: '0 10px',
    },
    buttonWrapper: {
      display: 'flex',
      gap: isMobile ? '10px' : '16px',
      justifyContent: 'center',
      flexWrap: 'wrap',
      alignItems: 'center',
      marginBottom: isMobile ? '32px' : '40px',
      padding: '0 10px',
    },
    primaryButton: {
      backgroundColor: '#ffffff',
      color: '#667eea',
      padding: isMobile ? '11px 24px' : '14px 36px',
      fontSize: isMobile ? '13px' : '16px',
      fontWeight: 'bold',
      border: 'none',
      borderRadius: '50px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
      whiteSpace: 'nowrap',
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: '#ffffff',
      padding: isMobile ? '11px 24px' : '14px 36px',
      fontSize: isMobile ? '13px' : '16px',
      fontWeight: 'bold',
      border: '2px solid #ffffff',
      borderRadius: '50px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      padding: '0 10px',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    feature: {
      color: '#ffffff',
      padding: isMobile ? '10px 6px' : '14px 10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    featureIcon: {
      fontSize: isMobile ? '28px' : '36px',
      marginBottom: isMobile ? '6px' : '10px',
      display: 'block',
      lineHeight: '1',
    },
    featureTitle: {
      fontSize: isMobile ? '13px' : '16px',
      fontWeight: 'bold',
      marginBottom: isMobile ? '3px' : '6px',
      lineHeight: '1.3',
    },
    featureText: {
      fontSize: isMobile ? '11px' : '13px',
      opacity: '0.9',
      lineHeight: '1.4',
    },
  };

  const features = [
    { icon: '⚡', title: 'Fast Delivery', text: 'Quick turnaround' },
    { icon: '🛡️', title: 'Secure Solutions', text: 'Enterprise-grade' },
    { icon: '💡', title: 'Innovation First', text: 'Cutting-edge tech' },
    { icon: '🤝', title: '24/7 Support', text: 'Always available' },
  ];

  return (
    <section style={styles.section} id="cta">
      <div style={styles.overlay}></div>
      
      <div style={styles.container}>
        <h2 style={styles.title}>Ready to Build Your Next Product?</h2>
        <p style={styles.description}>
          Let's discuss your idea and make it happen. Our team is ready to transform 
          your vision into reality with innovative solutions.
        </p>
        
        <div style={styles.buttonWrapper}>
          <button
            style={styles.primaryButton}
            onClick={() => setIsModalOpen(true)}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
              }
            }}
          >
            Contact Us
          </button>
          
          <button
            style={styles.secondaryButton}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.color = '#667eea';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#ffffff';
              }
            }}
          >
            View Our Work
          </button>
        </div>

        <div style={styles.featuresGrid}>
          {features.map((feature, index) => (
            <div key={index} style={styles.feature}>
              <div style={styles.featureIcon}>{feature.icon}</div>
              <div style={styles.featureTitle}>{feature.title}</div>
              <div style={styles.featureText}>{feature.text}</div>
            </div>
          ))}
        </div>
      </div>

      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </section>
  );
};

export default CTA;